import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Row, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl } from "../../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import moment from "moment";
import { exportToPDF } from "../../../Utils/pdfExportUtils";
import { excelExportUtils } from "../../../Utils/excelExportUtils";
interface CallHistory {
  id: string;
  start_time: string;
  end_time: string;
  customer: {
    mobile: string;
  };
  agent: {
    agent_name: string;
    id: string;
  };
  call_date: string;
  duration: number;
  customer_amount: number | string;
  agent_amount: number | string;
  formatted_duration: string;
  customer_amount_without_currency: number | string;
  agent_amount_without_currency: number | string;
}
interface Agent {
  id: number;
  agent_name: string;
}
interface CallHistoryWithFooter extends CallHistory {
  isFooter?: boolean;
}
export default function VideoCallReport() {
  const [filterText, setFilterText] = useState("");
  const [filteredItems, setFilteredItems] = useState<CallHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const { currency } = useCurrency();
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(filterText);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [filterText]);

  const fetchData = async (page = 1, perPage = 10, sortField = sortColumn, sortDir = sortOrder) => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}video_call_history/getallreport`, {
        params: {
          page,
          per_page: perPage,
          search: debouncedSearchTerm,
          from_date: fromDate ? fromDate.toLocaleDateString("en-CA") : null,
          to_date: toDate ? toDate.toLocaleDateString("en-CA") : null,
          agent_name: selectedAgent !== "" ? selectedAgent : null,
          sort_by: sortField,
          sort_order: sortDir,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log(response, "response");

      const data: CallHistory[] = response.data.data;
      setFilteredItems(data);
      setTotalRows(response.data.total);
      setCurrentPage(response.data.current_page);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await axios.get(`${baseUrl}agent/getall`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const filteredAgent = result.data.filter(
          (agent: any) => agent.is_deleted === 0 && agent.is_active === 1
        );
        setAgents(filteredAgent); // Set in state
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    fetchData(currentPage, perPage);
  }, [currentPage, perPage, selectedAgent, debouncedSearchTerm, fromDate, toDate]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData(page, perPage, sortColumn, sortOrder);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    await fetchData(1, newPerPage, sortColumn, sortOrder);
  };

  const handleSort = (column: any, sortDirection: string) => {
    const sortField = column.sortField || "created_at";

    setSortColumn(sortField);
    setSortOrder(sortDirection);
    setCurrentPage(1);
    fetchData(1, perPage, sortField, sortDirection);
  };

  const handleFullReset = () => {
    setFilterText("");
    setDebouncedSearchTerm("");
    setFromDate(null);
    setToDate(null);
    setSelectedAgent("");
    setCurrentPage(1);
  };

  const getCallHistoryColumns = () => {
    return [
      {
        name: <div style={{ whiteSpace: "normal" }}>S.No</div>,
        cell: (row: CallHistoryWithFooter, index: number) =>
          row.isFooter ? "" : (currentPage - 1) * perPage + index + 1,
        width: "60px",
      },
      {
        name: <div style={{ whiteSpace: "normal" }}>Customer Mobile</div>,
        selector: (row: CallHistory) => row.customer?.mobile,
        sortable: true,
        sortField: "customer_mobile",
      },
      {
        name: <div style={{ whiteSpace: "normal" }}>Agent Name</div>,
        selector: (row: CallHistory) => row.agent?.agent_name,
        sortable: true,
        sortField: "agent_name",
      },
      {
        name: <div style={{ whiteSpace: "normal" }}>Call Date</div>,
        sortable: true,
        sortField: "call_date",
        cell: (row: CallHistoryWithFooter) => {
          if (row.isFooter) {
            return <>Total</>;
          }
          const startFormatted = moment(row.start_time, "HH:mm:ss").format("hh:mmA");
          const endFormatted = moment(row.end_time, "HH:mm:ss").format("hh:mmA");
          return <>{`${row.call_date} ${startFormatted} - ${endFormatted}`}</>;
        },
      },
      {
        name: <div style={{ whiteSpace: "normal" }}>Duration (mins)</div>,
        sortable: true,
        sortField: "duration",
        cell: (row: CallHistoryWithFooter) => {
          if (row.isFooter) {
            return `${row.formatted_duration || row.duration}`;
          }
          return row.formatted_duration || "N/A";
        },
      },
      {
        name: (
          <div style={{ whiteSpace: "normal" }}>
            Customer Amount {currency ? `(${currency})` : ""}
          </div>
        ),
        selector: (row: CallHistory) => row.customer_amount_without_currency || "0",
        sortable: true,
        sortField: "customer_amount",
      },
      {
        name: (
          <div style={{ whiteSpace: "normal" }}>
            Agent Amount {currency ? `(${currency})` : ""}
          </div>
        ),
        selector: (row: CallHistory) => row.agent_amount_without_currency || "0",
        sortable: true,
        sortField: "agent_amount",
      },
    ];
  };


  const fetchAllDataWithoutPagination = async () => {
    try {
      // console.log(fromDate, toDate, filterText);
      const response = await axios.get(
        `${baseUrl}video_call_history/getallreportnopagination`,
        {
          params: {
            search: debouncedSearchTerm,
            from_date: fromDate ? fromDate.toISOString().split("T")[0] : null,
            to_date: toDate ? toDate.toISOString().split("T")[0] : null,
            agent_name: selectedAgent !== "" ? selectedAgent : null,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // console.log(response.data.data, "data");
      return response.data.data; // Return the fetched data
    } catch (error) {
      console.error("Error fetching all data without pagination:", error);
      return []; // Return an empty array on error
    }
  };

  const calculateTotals = () => {
    const totalDuration = filteredItems.reduce((acc, row) => {
      const start = new Date(`1970-01-01T${row.start_time}`);
      const end = new Date(`1970-01-01T${row.end_time}`);
      const diff = Math.floor((end.getTime() - start.getTime()) / 60000);
      return acc + diff;
    }, 0);

    const totalCustomerAmount = filteredItems.reduce((acc, row) => {
      const amount = parseFloat(
        (row as any).customer_amount_without_currency || "0"
      );
      return acc + amount;
    }, 0);

    const totalAgentAmount = filteredItems.reduce((acc, row) => {
      const amount = parseFloat(
        (row as any).agent_amount_without_currency || "0"
      );
      return acc + amount;
    }, 0);

    return {
      totalDuration,
      totalCustomerAmount: parseFloat(totalCustomerAmount.toFixed(2)),
      totalAgentAmount: parseFloat(totalAgentAmount.toFixed(2)),
    };
  };

  const { totalDuration, totalCustomerAmount, totalAgentAmount } =
    calculateTotals();
  const columnsData: CallHistoryWithFooter[] = [
    ...filteredItems,
    {
      isFooter: true,
      customer: { mobile: "" },
      agent: { agent_name: "", id: "footer" },
      call_date: "Total",
      duration: totalDuration,
      formatted_duration: `${totalDuration} mins`,
      customer_amount: `${currency ? ` ${currency}` : ""
        }${totalCustomerAmount}`,
      agent_amount: `${currency ? ` ${currency}` : ""}${totalAgentAmount}`,
      customer_amount_without_currency: totalCustomerAmount,
      agent_amount_without_currency: totalAgentAmount,
      id: "",
      start_time: "",
      end_time: "",
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };

  const conditionalRowStyles = [
    {
      when: (row: any) => row.isFooter === true,
      style: {
        fontWeight: "800",
        backgroundColor: "#f8f9fa",
        color: "#308e87",
        borderTop: "2px solid #dee2e6",
        fontSize: "16px",
      },
    },
  ];

  const exportPDF = async () => {
    const rawData = await fetchAllDataWithoutPagination();
    const allData = Array.isArray(rawData) ? rawData : Object.values(rawData);

    const tableData = allData.map((row) => {
      const start = new Date(`1970-01-01T${row.start_time}`);
      const end = new Date(`1970-01-01T${row.end_time}`);
      const duration = Math.floor((end.getTime() - start.getTime()) / 60000);

      return [
        row.customer?.mobile || "N/A",
        row.agent?.agent_name || "N/A",
        row.call_date,
        `${duration} mins`,
        `${row.customer_amount_without_currency}` || "N/A",
        `${row.agent_amount_without_currency}` || "N/A",
      ];
    });

    const footerRow = [
      "",
      "",
      "Total",
      `${totalDuration} mins`,
      `${totalCustomerAmount}`,
      `${totalAgentAmount}`,
    ];

    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      "€": "EUR",
      "£": "GBP",
      "¥": "YEN",
      "₽": "RUB",
      "₩": "KRW",
      "฿": "THB",
      "₪": "ILS",
      "₫": "VND",
      "₱": "PHP",
    };
    const pdfCurrency = currencyMap[currency] || currency;

    exportToPDF({
      fileName: "Video_Call_Report",
      columns: [
        "Customer Mobile",
        "Agent Name",
        "Call Date",
        "Duration",
        `Customer Amount ${pdfCurrency ? `(${pdfCurrency})` : ""}`,
        `Agent Amount ${pdfCurrency ? `(${pdfCurrency})` : ""}`,
      ],
      data: tableData,
      emptyDataMessage: "No video call report available for export.",
      footerRow,
      autoTableOptions: {
        headStyles: { fillColor: [0, 105, 92] },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          4: { halign: "right" },
          5: { halign: "right" },
        },
        didParseCell: (data: any) => {
          if (data.row.index === tableData.length) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [245, 245, 245];
          }
        },
      },
    });
  };

  const exportExcel = async () => {
    const rawData = await fetchAllDataWithoutPagination();
    const allData = Array.isArray(rawData) ? rawData : Object.values(rawData);
    if (!allData.length) {
      toast.warn("No call report available for export.", {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      return;
    }
    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      "€": "EUR",
      "£": "GBP",
      "¥": "YEN",
      "₽": "RUB",
      "₩": "KRW",
      "฿": "THB",
      "₪": "ILS",
      "₫": "VND",
      "₱": "PHP",
    };
    const excelCurrency = currencyMap[currency] || currency;
    const customerHeader = `Customer Amount ${excelCurrency ? `(${excelCurrency})` : ""}`;
    const agentHeader = `Agent Amount ${excelCurrency ? `(${excelCurrency})` : ""}`;

    const tableData = allData.map((row: CallHistory) => {
      const start = new Date(`1970-01-01T${row.start_time}`);
      const end = new Date(`1970-01-01T${row.end_time}`);
      const duration = Math.floor((end.getTime() - start.getTime()) / 60000);

      return {
        "Customer Mobile": row.customer?.mobile || "N/A",
        "Agent Name": row.agent?.agent_name || "N/A",
        "Call Date": row.call_date,
        Duration: `${duration} mins`,
        [customerHeader]: `${row.customer_amount_without_currency}` || "N/A",
        [agentHeader]: `${row.agent_amount_without_currency}` || "N/A",
      };
    });

    // Add footer row
    const footerRow = {
      "Customer Mobile": "",
      "Agent Name": "",
      "Call Date": "Total",
      Duration: `${totalDuration} mins`,
      [customerHeader]: totalCustomerAmount,
      [agentHeader]: totalAgentAmount,
    };
    excelExportUtils(tableData, "Video_Call_Report", "Video Call Report", footerRow);
  };

  return (
    <>
      <Breadcrumbs
        pageTitle="Call Report"
        parent="Reports"
        title="Call Report"
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            <Row className="align-items-center justify-content-end">
              <Col xl={2}>
                <div className="d-flex gap-2">
                  <Button
                    color="primary"
                    onClick={exportPDF}
                    className="d-flex justify-content-center align-items-center p-0"
                    style={{ width: "30px", height: "30px" }}
                    title="PDF"
                  >
                    <FaFilePdf size={16} />
                  </Button>
                  <Button
                    color="secondary"
                    onClick={exportExcel}
                    className="d-flex justify-content-center align-items-center p-0"
                    style={{ width: "30px", height: "30px" }}
                    title="EXCEL"
                  >
                    <FaFileExcel size={16} />
                  </Button>
                </div>
              </Col>

              <Col xl={2}>
                <div className="d-flex align-items-center">
                  <Input
                    type="select"
                    value={selectedAgent}
                    onChange={(e) => {
                      setSelectedAgent(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">Filter by agent</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.agent_name}>
                        {agent.agent_name}
                      </option>
                    ))}
                  </Input>
                </div>
              </Col>

              <Col xl={5}>
                <div className="d-flex align-items-center gap-2">
                  <label className="mb-0">From:</label>
                  <div style={{ flex: 1 }}>
                    <DatePicker
                      selected={fromDate}
                      onChange={(date: Date | null) => {
                        setFromDate(date);
                        setCurrentPage(1);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="Start date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                    />
                  </div>

                  <label className="mb-0">To:</label>
                  <div style={{ flex: 1 }}>
                    <DatePicker
                      selected={toDate}
                      onChange={(date: Date | null) => {
                        setToDate(date);
                        setCurrentPage(1);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="End date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                    />
                  </div>
                </div>
              </Col>

              <Col xl={3}>
                <div className="d-flex align-items-center">
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                  />
                </div>
              </Col>
            </Row>

            <div className="table-responsive custom-scrollbar mt-3">
              <DataTable
                data={columnsData}
                columns={getCallHistoryColumns()}
                highlightOnHover
                striped
                customStyles={customStyles}
                pagination
                paginationServer
                paginationPerPage={perPage}
                paginationTotalRows={totalRows}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                paginationDefaultPage={currentPage}
                sortServer
                onSort={handleSort}
                responsive
                progressPending={loading}
                progressComponent={
                  <div className="p-5">
                    <Spinner color="primary" />
                  </div>
                }
                conditionalRowStyles={conditionalRowStyles}
              />
            </div>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer />
    </>
  );
}
